import React, { useEffect, useState } from 'react';
import { Button, Input, Space } from 'antd';

interface Props {
  value?: Record<string, string>;
  onChange: (val: Record<string, string>) => void;
  autoIndexKeys?: boolean; // when true, keys are 1..n and not editable
}

const KeyValueListEditor: React.FC<Props> = ({ value, onChange, autoIndexKeys }) => {
  const entries = Object.entries(value || {}) as [string, string][];
  const sorted: [string, string][] = autoIndexKeys
    ? [...entries].sort((a, b) => (parseInt(a[0]) || 0) - (parseInt(b[0]) || 0))
    : entries;
  const initRows: [string, string][] = sorted.length ? (sorted as [string, string][]) : [[autoIndexKeys ? '1' : '', '']];
  const [rows, setRows] = useState<[string, string][]>(initRows);

  useEffect(() => {
    const freshEntries = Object.entries(value || {}) as [string, string][];
    const sortedFresh: [string, string][] = autoIndexKeys
      ? [...freshEntries].sort((a, b) => (parseInt(a[0]) || 0) - (parseInt(b[0]) || 0))
      : freshEntries;
    const normalized: [string, string][] = sortedFresh.length ? (sortedFresh as [string, string][]) : [[autoIndexKeys ? '1' : '', '']];
    const renumbered: [string, string][] = autoIndexKeys
      ? normalized.map(([, v], i) => [String(i + 1), v] as [string, string])
      : normalized;
    setRows(renumbered as [string, string][]);
  }, [value, autoIndexKeys]);

  const emit = (rowsData: [string, string][]) => {
    const record: Record<string, string> = {};
    rowsData.forEach(([k, v], i) => {
      const key = autoIndexKeys ? String(i + 1) : (k || '').trim();
      if (autoIndexKeys) {
        if ((v || '').trim()) record[key] = v;
      } else if (key) {
        record[key] = v;
      }
    });
    onChange(record);
  };

  const updateKey = (index: number, key: string) => {
    const next = rows.map((r, i) => (i === index ? [autoIndexKeys ? String(i + 1) : key, r[1]] : r)) as [string, string][];
    setRows(next);
    // Do not emit on key typing to allow spaces; emit on blur
  };

  const commitKeys = () => {
    // Emit current rows when user finishes editing keys (onBlur)
    emit(rows);
  };

  const updateValue = (index: number, val: string) => {
    const next = rows.map((r, i) => (i === index ? [r[0], val] : r)) as [string, string][];
    setRows(next);
    emit(next);
  };

  const addRow = () => {
    const next = [...rows, [autoIndexKeys ? String(rows.length + 1) : '', '']] as [string, string][];
    setRows(next);
    // Do not emit here; wait until user enters content
  };

  const removeRow = (index: number) => {
    let next = rows.filter((_, i) => i !== index) as [string, string][];
    if (!next.length) next = [[autoIndexKeys ? '1' : '', '']];
    if (autoIndexKeys) next = next.map(([, v], i) => [String(i + 1), v]);
    setRows(next);
    emit(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map(([k, v], idx) => (
        <Space key={idx} align="baseline" style={{ display: 'flex' }}>
          {autoIndexKeys ? (
            <Input value={k} disabled style={{ width: 80 }} />
          ) : (
            <Input
              placeholder="Key"
              value={k}
              style={{ width: 180 }}
              onChange={(e) => updateKey(idx, e.target.value)}
              onBlur={commitKeys}
            />
          )}
          <Input
            placeholder="Value"
            value={v}
            style={{ minWidth: 240 }}
            onChange={(e) => updateValue(idx, e.target.value)}
          />
          <Button danger onClick={() => removeRow(idx)}>Remove</Button>
        </Space>
      ))}
      <Button onClick={addRow}>Add Row</Button>
    </div>
  );
};

export { KeyValueListEditor };
